import React from 'react';
import site, { services } from '../../data/site';
import { owner } from '../../data/media';
import { Section, SectionHeading } from '../ui/Section';
import Reveal from '../ui/Reveal';
import Icon from '../ui/Icon';
import DotTrail from '../ui/DotTrail';
import Parallax from '../ui/Parallax';
import { TextReveal } from '../ui/TextReveal';

/**
 * "The House" — who you are actually buying from, and what the counter offers.
 * The owner photograph anchors it, because the brief's promise is trust, and
 * trust in a jewellery showroom is a face rather than a feature list.
 */
export function House() {
  return (
    <Section id="house" tone="soft">
      <div className="shell">
        <div className="grid gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-20">
          {/* ---- Portraits ---- */}
          <Reveal variant="left" distance={28} duration={950} className="relative">
            {/* The frame stays put while the photograph inside drifts, so the
                offset border reads as a fixed mount. */}
            <div className="relative overflow-hidden rounded-sm shadow-lift">
              <Parallax speed={-22} scale={0.05}>
                <img
                  src={owner.src}
                  alt={owner.alt}
                  loading="lazy"
                  decoding="async"
                  width={1200}
                  height={900}
                  className="w-full object-cover"
                />
              </Parallax>
            </div>
            <div
              className="absolute -bottom-4 -left-4 -z-10 h-full w-full rounded-sm border border-champagne-300/60"
              aria-hidden="true"
            />
          </Reveal>

          {/* ---- Copy ---- */}
          <div>
            <SectionHeading
              eyebrow="The House"
              title={['Bought across', 'a counter, in person']}
              lede="Rajendra Jewellers Pvt. Ltd. is a showroom business, not a catalogue. You sit down, pieces come out of the case, and someone who knows the metal and the stones talks you through them."
            />

            <Reveal delay={140}>
              <p className="mt-6 max-w-xl text-[0.95rem] leading-relaxed text-ink-700/80">
                We hold gold and diamond jewellery, antique and temple-work gold, and branded
                lines side by side — so a comparison is a matter of putting two trays next to
                each other rather than opening two tabs. Valuation is done in-house by a Govt.
                Approved Valuer.
              </p>
            </Reveal>

            <Reveal delay={200} className="mt-8">
              <DotTrail count={6} />
            </Reveal>

            <div className="mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-2">
              {services.map((service, i) => (
                <Reveal key={service.title} variant="right" distance={22} delay={240 + i * 90}>
                  <div className="flex gap-4">
                    <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full border border-champagne-300/60 text-sun-500">
                      <Icon name={service.icon} size={18} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg leading-snug text-ink-950">
                        {service.title}
                      </h3>
                      <p className="mt-1.5 text-[0.86rem] leading-relaxed text-ink-700/75">
                        {service.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Pull quote — the brand tagline, given room. */}
        <figure className="relative mx-auto mt-24 max-w-3xl text-center lg:mt-32">
          <Reveal variant="scale" duration={900}>
            <Icon
              name="quote"
              size={38}
              className="mx-auto mb-6 text-champagne-300"
              strokeWidth={0}
              fill="currentColor"
            />
          </Reveal>
          <blockquote className="font-display text-3xl italic leading-tight text-ink-950 sm:text-[2.75rem]">
            <TextReveal lines={[site.tagline]} delay={120} duration={1000} />
          </blockquote>
          <Reveal variant="fade" delay={420}>
            <figcaption className="mt-6 text-[0.65rem] uppercase tracking-mega text-champagne-500">
              {site.legalName}
            </figcaption>
          </Reveal>
        </figure>
      </div>
    </Section>
  );
}

export default House;
